/* eslint-disable global-require */
import React from "react";
import {
  Button,
  CircularProgress,
  Grid,
  Typography,
  Card,
  CardContent,
  CardActions,
  Box,
  LinearProgress,
} from "@material-ui/core";
import {
  GetApp as DownloadIcon,
  Group as UsersIcon,
  Timeline as TiersIcon,
  MonetizationOn as AdvancesIcon,
  AccountTree as StructureIcon,
  Description as ContractsIcon,
  Assignment as CommissionsIcon,
} from "@material-ui/icons";
import { useCommissionContext } from "../../../../../contexts/CommissionContext";

/**
 * Component that renders a series of export buttons for commissions data.
 * The component uses the `useCommissionContext` hook to get the export functions
 * and status information.
 *
 * The component renders a grid of 3 columns, with each column containing a card
 * with a button to export the commissions data. The button is disabled while the
 * export is in progress.
 *
 * The component also renders a linear progress bar below each button, showing the
 * progress of the export.
 */
export default function ExportButtons() {
  const {
    handleExport,
    handlePaginatedDealerExport,
    exportLoading,
    exportProgress,
    exportDetails,
    totalUsers,
    cutoffDate,
  } = useCommissionContext();

  // Array of export options for dynamic rendering
  const exportOptions = [
    {
      id: "fibreComms",
      title: "Fibre Agent Commissions",
      description: "Export commissions data for fibre agents",
      icon: <CommissionsIcon />,
      handler: () =>
        handleExport(
          "fibreComms",
          (cutoff, callback) =>
            commissionService.exportFibreCommsReport(cutoff, callback),
          cutoffDate
        ),
      loading: exportLoading.fibreComms,
      details: exportDetails.fibreComms,
    },
    {
      id: "fibreLeaderComms",
      title: "Fibre Leader Commissions",
      description: "Export commissions data for fibre leaders",
      icon: <CommissionsIcon />,
      handler: () =>
        handleExport(
          "fibreLeaderComms",
          (cutoff, callback) =>
            commissionService.exportFibreLeaderCommsReport(cutoff, callback),
          cutoffDate
        ),
      loading: exportLoading.fibreLeaderComms,
      details: exportDetails.fibreLeaderComms,
    },
    {
      id: "dealers",
      title: "Users",
      description: "Export all user data with pagination",
      icon: <UsersIcon />,
      handler: handlePaginatedDealerExport,
      loading: exportLoading.dealers,
      progress: exportProgress,
      progressInfo:
        exportProgress > 0 && totalUsers > 0
          ? `Exporting ${Math.min(
              (exportProgress * totalUsers) / 100,
              totalUsers
            ).toFixed(0)} of ${totalUsers} users`
          : null,
    },
    {
      id: "agentTiers",
      title: "Agent Tiers",
      description: "TODO: Calculate and export agent tier data", // need Jeandre's help
      icon: <TiersIcon />,
      handler: () =>
        handleExport(
          "agentTiers",
          (cutoff, callback) =>
            commissionService.exportAgentTiers(cutoff, callback),
          cutoffDate
        ),
      loading: exportLoading.agentTiers,
      details: exportDetails.agentTiers,
    },
    {
      id: "advances",
      title: "Advances",
      description: "Export current advances data",
      icon: <AdvancesIcon />,
      handler: () =>
        handleExport("advances", callback =>
          commissionService.exportAdvances(callback)
        ),
      loading: exportLoading.advances,
      details: exportDetails.advances,
    },
    {
      id: "structure",
      title: "Dealer Structure",
      description: "Export hierarchical dealer structure",
      icon: <StructureIcon />,
      handler: () =>
        handleExport("structure", callback =>
          commissionService.exportStructure(callback)
        ),
      loading: exportLoading.structure,
      details: exportDetails.structure,
    },
    {
      id: "contracts",
      title: "Dealer Contracts",
      description: "Download dealer contract information",
      icon: <ContractsIcon />,
      handler: () =>
        handleExport("contracts", callback =>
          commissionService.exportDealerContracts(callback)
        ),
      loading: exportLoading.contracts,
      details: exportDetails.contracts,
    },
  ];

  // Helper to import service dynamically
  const commissionService = React.useMemo(
    () => require("../../../../../services/commissions.service").default,
    []
  );

  return (
    <Grid container spacing={2}>
      {exportOptions.map(option => (
        <Grid item xs={12} sm={6} md={4} key={option.id}>
          <Card
            variant="outlined"
            style={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <CardContent style={{ flexGrow: 1 }}>
              <Box display="flex" alignItems="center" mb={1}>
                <Box color="secondary.main" mr={1}>
                  {option.icon}
                </Box>
                <Typography variant="h6" component="h3">
                  {option.title}
                </Typography>
              </Box>

              <Typography
                variant="body2"
                color="textSecondary"
                style={{ minHeight: 40 }}
              >
                {option.description}
              </Typography>

              {option.loading && option.details && (
                <Box mt={1}>
                  <Typography variant="caption" display="block">
                    Stage: {option.details.stage || "Processing"} •{" "}
                    {option.details.progress || 0}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={option.details.progress || 0}
                    style={{ height: 4, borderRadius: 2 }}
                  />
                </Box>
              )}

              {option.loading && option.progress && (
                <Box mt={1}>
                  <Typography variant="caption" display="block">
                    Progress: {option.progress}%
                  </Typography>
                  {option.progressInfo && (
                    <Typography
                      variant="caption"
                      display="block"
                      color="textSecondary"
                    >
                      {option.progressInfo}
                    </Typography>
                  )}
                  <LinearProgress
                    variant="determinate"
                    value={option.progress}
                    style={{ height: 4, borderRadius: 2 }}
                  />
                </Box>
              )}
            </CardContent>

            <CardActions style={{ marginTop: "auto" }}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                disabled={option.loading || exportLoading.batchExport}
                onClick={option.handler}
                startIcon={
                  option.loading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DownloadIcon />
                  )
                }
                size="small"
              >
                {option.loading ? "Exporting..." : "Export"}
              </Button>
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}
